import { type CSSProperties, type ReactNode, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, ErrorState } from '@ui-kit';
import { useFileBackendInfo } from '../hooks/useFileBackendInfo';
import { useFileUpload } from '../hooks/useFileUpload';
import { usePresign } from '../hooks/usePresign';
import { BackendBadge } from '../components/BackendBadge';
import { IntegrationCommonUsagePanel } from '../components/IntegrationCommonUsagePanel';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { integrationHubApi } from '../api/integrationHubApi';
import type { FileUploadResultDto, PresignResultDto, ApiError } from '../types/integrationHub';

/**
 * FileBackendConsolePage — 스토리지 어댑터 콘솔 (/integration-hub/storage)
 * 5상태: loading / empty(N/A) / error / default / dialog(N/A - 인라인 상태)
 */
export function FileBackendConsolePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<FileUploadResultDto | null>(null);
  const [uploadError, setUploadError] = useState<ApiError | null>(null);

  const [presignResult, setPresignResult] = useState<PresignResultDto | null>(null);
  const [presignError, setPresignError] = useState<ApiError | null>(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const backendQuery = useFileBackendInfo();
  const uploadMutation = useFileUpload();
  const presignMutation = usePresign();

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleUpload = useCallback(() => {
    if (!selectedFile) return;
    setUploadError(null);
    setUploadResult(null);
    setPresignResult(null);
    setPresignError(null);
    uploadMutation.mutate(selectedFile, {
      onSuccess: (data) => {
        setUploadResult(data);
        showToast(t('integrationHub.toast.uploaded'));
      },
      onError: (err) => {
        setUploadError(err as ApiError);
      },
    });
  }, [selectedFile, uploadMutation, showToast, t]);

  const handleDownload = useCallback(async () => {
    if (!uploadResult?.fileId) return;
    setIsDownloading(true);
    try {
      const { data, filename } = await integrationHubApi.downloadFile(uploadResult.fileId);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const apiErr = err as ApiError;
      showToast(t(`integrationHub.error.${apiErr.code}`, { defaultValue: t('integrationHub.error.network') }));
    } finally {
      setIsDownloading(false);
    }
  }, [uploadResult, showToast, t]);

  const handlePresign = useCallback(() => {
    if (!uploadResult?.fileId) return;
    setPresignError(null);
    setPresignResult(null);
    presignMutation.mutate(uploadResult.fileId, {
      onSuccess: (data) => {
        setPresignResult(data);
      },
      onError: (err) => {
        setPresignError(err as ApiError);
      },
    });
  }, [uploadResult, presignMutation]);

  const backendInfo = backendQuery.data;
  const isLoading = backendQuery.isLoading;
  const isError = backendQuery.isError;
  const is403 = isError && (backendQuery.error as unknown as ApiError)?.code === 'AUTH-403-001';

  return (
    <div style={{ padding: 0 }}>
      <PageHeader
        title={t('integrationHub.storage.title')}
        breadcrumb={<span>{t('integrationHub.storage.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button
              type="button"
              onClick={() => navigate('/integration-hub')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.endpoints')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/integration-hub/history')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.history')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/integration-hub/tcp')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.tcp')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/integration-hub/grpc')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.grpc')}
            </button>
          </div>
        }
      />

      {/* loading skeleton */}
      {isLoading && (
        <div
          aria-label={t('integrationHub.storage.loadingAriaLabel')}
          aria-busy="true"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
            padding: 'var(--spacing-6)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 20,
                width: `${40 + (i * 15) % 40}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
      )}

      {/* error */}
      {isError && (
        <div style={{ padding: 'var(--spacing-6)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)' }}>
          {is403 ? (
            <ErrorState
              title={t('integrationHub.error.AUTH-403-001')}
              message={t('integrationHub.error.forbidden')}
              onRetry={() => navigate('/')}
            />
          ) : (
            <ErrorState
              title={t('integrationHub.error.loadFailed')}
              message={(backendQuery.error as unknown as ApiError)?.message ?? t('integrationHub.error.network')}
              onRetry={() => void backendQuery.refetch()}
            />
          )}
        </div>
      )}

      {/* default */}
      {!isLoading && !isError && backendInfo && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          {/* backend 정보 카드 */}
          <section
            aria-label={t('integrationHub.storage.currentBackend')}
            style={cardStyle}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--spacing-4)' }}>
              <InfoField label={t('integrationHub.storage.currentBackend')}>
                <BackendBadge backend={backendInfo.backend} />
              </InfoField>
              <InfoField label={t('integrationHub.storage.presignSupported')}>
                <span style={{ color: backendInfo.presignSupported ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                  {backendInfo.presignSupported
                    ? `✓ ${t('integrationHub.storage.presignYes')}`
                    : `✗ ${t('integrationHub.storage.presignNo')}`}
                </span>
              </InfoField>
              <InfoField label={t('integrationHub.storage.demoMode')}>
                <span style={{ color: backendInfo.demoMode ? 'var(--color-warning, orange)' : 'var(--color-success)' }}>
                  {backendInfo.demoMode ? 'ON' : 'OFF'}
                </span>
              </InfoField>
              <InfoField label={t('integrationHub.storage.availableBackends')}>
                <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                  {backendInfo.availableBackends.join(' · ')}
                </span>
              </InfoField>
            </div>
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-3)', borderTop: '1px solid var(--color-surface-border)', paddingTop: 'var(--spacing-3)' }}>
              {t('integrationHub.storage.switchHint')}
            </p>

            {/* backend 설정 표시 */}
            {Object.keys(backendInfo.properties).length > 0 && (
              <details style={{ marginTop: 'var(--spacing-3)' }}>
                <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                  {t('integrationHub.storage.properties')}
                </summary>
                <pre
                  style={{
                    marginTop: 'var(--spacing-2)',
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--spacing-3)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-family-mono)',
                    overflowX: 'auto',
                    color: 'var(--color-text-primary)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(backendInfo.properties, null, 2)}
                </pre>
              </details>
            )}
          </section>

          {/* 파일 업로드 섹션 */}
          <section aria-label={t('integrationHub.storage.upload.title')} style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('integrationHub.storage.upload.title')}</h3>
            <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                id="storage-file-input"
                aria-label={t('integrationHub.storage.upload.label')}
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={secondaryButtonStyle}
              >
                {t('integrationHub.storage.upload.label')}
              </button>
              {selectedFile && (
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  {selectedFile.name} ({formatBytes(selectedFile.size)})
                </span>
              )}
              <ButtonGuard requirePermission="INTEGRATION_INVOKE">
                {({ disabled, reason }) => (
                  <button
                    type="button"
                    disabled={disabled || !selectedFile || uploadMutation.isPending}
                    title={reason}
                    onClick={handleUpload}
                    style={{
                      ...primaryButtonStyle,
                      opacity: (disabled || !selectedFile || uploadMutation.isPending) ? 0.6 : 1,
                      cursor: (disabled || !selectedFile || uploadMutation.isPending) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {uploadMutation.isPending ? '...' : t('integrationHub.storage.button.upload')}
                  </button>
                )}
              </ButtonGuard>
            </div>

            {/* 업로드 오류 */}
            {uploadError && (
              <div
                role="alert"
                style={{
                  marginTop: 'var(--spacing-3)',
                  padding: 'var(--spacing-3)',
                  border: '1px solid var(--color-danger)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-danger)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {t(`integrationHub.error.${uploadError.code}`, { defaultValue: uploadError.message })}
              </div>
            )}

            {/* 업로드 결과 */}
            {uploadResult && (
              <div
                style={{
                  marginTop: 'var(--spacing-4)',
                  padding: 'var(--spacing-4)',
                  background: 'var(--color-surface-muted)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--spacing-3)', color: 'var(--color-text-primary)' }}>
                  {t('integrationHub.storage.upload.result')}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--spacing-3)' }}>
                  <ResultField label={t('integrationHub.storage.upload.result.fileId')} value={
                    <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', wordBreak: 'break-all' }}>
                      {uploadResult.fileId}
                    </code>
                  } />
                  <ResultField label={t('integrationHub.storage.upload.result.originalName')} value={uploadResult.originalName} />
                  <ResultField label={t('integrationHub.storage.upload.result.size')} value={formatBytes(uploadResult.size)} />
                  <ResultField label={t('integrationHub.storage.upload.result.checksum')} value={
                    <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                      {uploadResult.checksum.slice(0, 16)}...
                    </code>
                  } />
                  <ResultField label={t('integrationHub.storage.upload.result.backend')} value={
                    <BackendBadge backend={uploadResult.backend} />
                  } />
                </div>

                {/* 다운로드 / presign / 삭제 액션 */}
                <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={isDownloading}
                    onClick={handleDownload}
                    style={{
                      ...secondaryButtonStyle,
                      opacity: isDownloading ? 0.6 : 1,
                      cursor: isDownloading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isDownloading ? '...' : t('integrationHub.storage.button.download')}
                  </button>
                  <ButtonGuard requirePermission="INTEGRATION_INVOKE">
                    {({ disabled, reason }) => (
                      <button
                        type="button"
                        disabled={disabled || presignMutation.isPending}
                        title={reason}
                        onClick={handlePresign}
                        style={{
                          ...secondaryButtonStyle,
                          opacity: (disabled || presignMutation.isPending) ? 0.6 : 1,
                          cursor: (disabled || presignMutation.isPending) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {presignMutation.isPending ? '...' : t('integrationHub.storage.button.presign')}
                      </button>
                    )}
                  </ButtonGuard>
                </div>

                {/* presign 오류 (INTGHUB-501-001 — error 상태) */}
                {presignError && (
                  <div
                    role="alert"
                    aria-describedby="presign-error-desc"
                    style={{
                      marginTop: 'var(--spacing-3)',
                      padding: 'var(--spacing-3)',
                      border: '1px solid var(--color-warning, orange)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-surface-muted)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    <strong style={{ color: 'var(--color-warning, orange)' }}>
                      ⚠
                    </strong>{' '}
                    <span id="presign-error-desc">
                      {presignError.code === 'INTGHUB-501-001'
                        ? t('integrationHub.storage.presign.unsupported', { backend: backendInfo.backend })
                        : t(`integrationHub.error.${presignError.code}`, { defaultValue: presignError.message })}
                    </span>
                  </div>
                )}

                {/* presign 결과 */}
                {presignResult && (
                  <div
                    style={{
                      marginTop: 'var(--spacing-3)',
                      padding: 'var(--spacing-3)',
                      border: '1px solid var(--color-success, green)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-surface-muted)',
                    }}
                  >
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                      {t('integrationHub.storage.presign.result.url')}
                    </p>
                    <a
                      href={presignResult.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)', wordBreak: 'break-all' }}
                    >
                      {presignResult.url}
                    </a>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-2)' }}>
                      {t('integrationHub.storage.presign.result.expiresAt')}: {new Date(presignResult.expiresAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {/* 공통 모듈 패널 */}
      <IntegrationCommonUsagePanel />

      {/* 토스트 */}
      {toast !== null && (
        <div role="alert" aria-live="polite" style={toastStyle}>
          {toast}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          section { padding: var(--spacing-3); }
        }
      `}</style>
    </div>
  );
}

function InfoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
        {label}
      </p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
        {children}
      </div>
    </div>
  );
}

function ResultField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
        {label}
      </p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// Shared styles
const tabButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const primaryButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const cardStyle: CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const toastStyle: CSSProperties = {
  position: 'fixed',
  bottom: 'var(--spacing-6)',
  right: 'var(--spacing-6)',
  zIndex: 1100,
  background: 'var(--color-surface-overlay, #1e1e2e)',
  color: 'var(--color-text-inverse)',
  padding: 'var(--spacing-3) var(--spacing-5)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  boxShadow: 'var(--shadow-lg)',
  maxWidth: 360,
};
